import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { Suspense, ErrorBoundary } from '~components/suspense';
import * as REST from '~services/rest';
import PromptService from '~services/prompt';
import { useSearchParams } from '~hooks/common';
import NotificationsActions from '~redux/notifications';

import ChatMain from './partials/main';
import ChatOptions from './partials/options';
import ChatHeader from './partials/header';
import ChatSkeleton from './partials/skeleton';

import { ChatContext } from './context';

import MobileInlineChatReview from '~containers/review/MobileInlineChatReview';

import './style.scss';

const PER_PAGE = 10;
const CHAT_ACTION_REJECT = 'REJECT'; // API action enum value for rejecting/closing a chat

const ChatContainer = ({ currentUser, recipient }) => {
  const [discussion, setDiscussion] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showMobileReview, setShowMobileReview] = useState(true);
  const { getAccessTokenSilently } = useAuth0();
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const { action } = useSearchParams('action');
  const [hasProcessedAction, setHasProcessedAction] = useState(false);

  const onDiscussionInitial = (data) => {
    const discussionId = data?.id || null;
    const discussionInitial = {
      discussionId,
      createdAt: data?.created_timestamp || Math.floor(Date.now() / 1000),
      acceptanceState: data?.acceptance_state,
      isRejected: data?.is_rejected || false,
      rejectionReason: data?.rejection_reason || null,
      acceptedAt: data?.accepted_at || null,
      isReported: false,
      participants: {
        recipient: {
          ...recipient,
          hasBlockedChat: data?.participants?.find((p) => p.id === recipient.personId)?.has_blocked_chat || false,
          isDeleted: data?.participants?.find((p) => p.id === recipient.personId)?.is_deleted || false,
        },
        currentUser: {
          personId: currentUser.personId,
          firstName: currentUser.firstName,
          profilePicture: currentUser.profilePicture,
          lastLogin: Math.floor(Date.now() / 1000),
          hasBlockedChat: data?.participants?.find((p) => p.id === currentUser.personId)?.has_blocked_chat || false,
          isOnline: true,
        },
      },
    };

    setDiscussion(discussionInitial);
    setIsLoading(false);
  };

  // Fetch discussionId
  useEffect(async () => {
    try {
      const token = await getAccessTokenSilently();
      const result = await REST.get({
        name: `chats/`,
        mockFileName: 'chats/get-chats',
        shouldThrowError: true,
        params: {
          'participant-id': currentUser.personId,
        },
        token,
      });``
      const existingDiscussion = result.data.find((d) => d.participants.find((f) => f.id === recipient.personId));
    //   console.log('existingDiscussion',existingDiscussion)
    //   console.log('currentUser',currentUser)
      
      const cUser = currentUser.accountType == 'client' ? 'customer' : 'worker';
      const isPendingForThisUser = existingDiscussion?.acceptance_pending_for == cUser;
      const isAcceptancePanding = existingDiscussion?.acceptance_state == 'pending';
    //   console.log('isPendingForThisUser',isPendingForThisUser)
    //   console.log('isAcceptancePanding',isAcceptancePanding)

      if ( isPendingForThisUser && isAcceptancePanding  ) {
        const confirmation = await PromptService.threeWayConfirm({
          title: intl.formatMessage({ id: 'containers.discussion.chat-request.title', defaultMessage: 'Chat Request' }),
          content: intl.formatMessage({
            id: 'containers.discussion.chat-request.content',
            defaultMessage: 'You need to accept the chat request to start chatting ',
          }),
          ok: intl.formatMessage({ id: 'containers.discussion.chat-request.accept', defaultMessage: 'Accept' }),
          cancel: intl.formatMessage({ id: 'containers.discussion.chat-request.reject', defaultMessage: 'Reject' }),
          className: 'c-modal__container',
        });
        // console.log('confirmation', confirmation);

        if (confirmation === 'close') {
          history.replace('/chats');
        } else if (existingDiscussion && existingDiscussion.id) {
          const action = confirmation === 'ok' ? 'ACCEPT' : 'REJECT';
          const response = await REST.post({
            name: `chats/${existingDiscussion.id}`,
            params: {
              action,
              who: currentUser.personId,
            },
            token,
            requiresAuthentication: true,
          });
        //   console.log(response);
          
          if (action === 'ACCEPT') {
            if (response.data[0].reason == 'exhaustion') {
              const warning = await PromptService.confirm({
                title: intl.formatMessage({ id: 'containers.discussion.chat-request.title', defaultMessage: 'Chat Can\'t be Accepted' }),
                content: response.status_msg,
                className: 'c-modal__container',
              });
            history.replace('/chats');
            } else {
              existingDiscussion.acceptance_state = 'accepted'; 
              onDiscussionInitial(existingDiscussion);
            }
          } else {
            history.replace('/chats');
          }
        }
      } else {
        onDiscussionInitial(existingDiscussion);
      }
    } catch (err) {
      onDiscussionInitial(null);
    }
  }, []);

  const handleNotInterested = useCallback(async () => {
    if (!discussion?.discussionId) return;
    const confirmResult = await PromptService.confirm({
      title: intl.formatMessage({ id: 'containers.discussion.not-interested.title', defaultMessage: 'Not Interested' }),
      content: intl.formatMessage({
        id: 'containers.discussion.not-interested.confirm',
        defaultMessage: 'Are you sure you are not interested? The customer will be informed.',
      }),
      ok: intl.formatMessage({ id: 'containers.discussion.not-interested.confirm-btn', defaultMessage: 'Confirm' }),
      cancel: intl.formatMessage({ id: 'containers.discussion.not-interested.cancel-btn', defaultMessage: 'Cancel' }),
      className: 'c-modal__container',
    });

    if (confirmResult === true) {
      try {
        const token = await getAccessTokenSilently();
        await REST.post({
          name: `chats/${discussion.discussionId}`,
          params: {
            action: CHAT_ACTION_REJECT,
            who: currentUser.personId,
            reason: 'not_interested',
          },
          token,
          requiresAuthentication: true,
        });

        dispatch(
          NotificationsActions.addNotification(
            intl.formatMessage({
              id: 'containers.discussion.not-interested.success',
              defaultMessage: 'Customer has been informed.',
            }),
            false
          )
        );

        setDiscussion((prev) => ({
          ...prev,
          isRejected: true,
          rejectionReason: 'not_interested',
          acceptanceState: 'rejected',
        }));
      } catch (err) {
        console.error('Error marking not interested:', err);
        dispatch(
          NotificationsActions.addNotification(
            intl.formatMessage({
              id: 'containers.discussion.not-interested.error',
              defaultMessage: 'Failed to process request. Please try again.',
            }),
            false
          )
        );
      }
    }
  }, [discussion?.discussionId, currentUser.personId, getAccessTokenSilently, intl, dispatch]);

  // Handle action=not-interested query parameter
  useEffect(() => {
    if (!discussion || hasProcessedAction) {
      return;
    }

    if (action === 'not-interested') {
      setHasProcessedAction(true);

      // Clean query param from URL
      const params = new URLSearchParams(history.location.search);
      params.delete('action');
      history.replace({
        pathname: history.location.pathname,
        search: params.toString(),
      });

      const isWorker = currentUser.accountType === 'worker';
      const isAccepted = discussion.acceptanceState === 'accepted';
      const isRejected = discussion.isRejected || discussion.acceptanceState === 'rejected';

      if (isWorker && isAccepted && !isRejected) {
        handleNotInterested();
      }
    }
  }, [discussion, action, hasProcessedAction, currentUser.accountType, handleNotInterested, history]);

  // Handle completely hiding the review section
  const handleCloseReview = () => {
    setShowMobileReview(false);
  };

  // Function to show the review component if it's hidden
  const handleShowReview = () => {
    setShowMobileReview(true);
  };

  return (
    <div className="c-chat-container">
      <ChatContext.Provider
        value={{
          recipient,
          currentUser,
          updateDiscussion: setDiscussion,
          discussion,
          messages,
          updateMessages: setMessages,
          showReview: handleShowReview,
          onNotInterested: handleNotInterested,
        }}
      >
        <div className="c-chat-container__header">
          <ChatHeader isLoading={isLoading} />
          <ChatOptions isLoading={isLoading} />
        </div>
        <div className="c-chat__mobile-review">
          {showMobileReview && (
            <MobileInlineChatReview
              authorId={currentUser.personId}
              receiverId={recipient.personId}
              receiverFirstName={recipient.firstName}
              onClose={handleCloseReview}
            />
          )}
        </div>
        <Suspense fallback={<ChatSkeleton />} isLoading={isLoading}>
          <ErrorBoundary fallback={<h2 className="l-error">Could not fetch chat data</h2>}>
            <ChatMain perPage={PER_PAGE} />
          </ErrorBoundary>
        </Suspense>
      </ChatContext.Provider>
    </div>
  );
};

export default ChatContainer;
